/**
 * ENG-9802 — which channel env changes must interrupt a live session.
 *
 * THE DEFECT. An MCP child reads its env from `process.env` exactly once, at
 * module load. The manager re-renders `.mcp.json` whenever a channel's config
 * hash moves (ENG-6600) — but nothing restarts the session, so the running
 * child keeps its boot-time values. An operator edits a chat allowlist in the
 * console, every signal says applied (console, `Channel credentials written …
 * converged`, the bytes on disk), and the agent enforces the old policy until
 * something unrelated respawns it. Measured 2026-09-01: all 12 agents recovered
 * that day needed a hand-rolled `tmux kill-session` AFTER their credentials
 * converged, `sophie` among them — her allowlist only became live at 11:39:46,
 * six minutes after the write. That manual restart is also the proof that a
 * respawn is SUFFICIENT, not merely necessary.
 *
 * WHY THIS IS A LIST AND NOT A RULE. The mechanical question — "is this value
 * frozen at boot?" — does not discriminate: measured against
 * `packages/mcp/src/telegram-channel.ts`, **all 13 telegram env keys are read at
 * module load**. A rule keyed on staleness would restart on an emoji change.
 * The real question is which changes are worth interrupting a live session for,
 * and that is a judgement per key, so it is written down per key.
 *
 * WHY IT LIVES HERE, next to the adapter that emits the env, rather than in the
 * manager that consumes it: a key added to the adapter and not classified here
 * would be silently inert — reproducing this very bug for that key, with no
 * signal. `channel-policy-env.test.ts` fails when the adapter emits a telegram
 * key this file does not classify, so the two cannot drift apart (the ENG-8231
 * allowlist-parity lesson).
 *
 * WHY NOT THE DRIFT WATCHER. `decideMcpRestartOnDrift` (ENG-5537) restarts only
 * on MCP server MEMBERSHIP change and suppresses value-only drift on purpose:
 * restarting on value-only drift is what flapped agents into the ENG-5441
 * circuit breaker (phil, agt-aws-1, 2026-05-25). Composio re-signs `mcp_url` on
 * most polls, and that must never bounce a session. This classification is an
 * orthogonal trigger keyed on named policy values, so credential churn cannot
 * reach it — the same shape as `decideSenderPolicyRestart` (ENG-5858), whose
 * header describes this identical gap for `SLACK_SENDER_POLICY`.
 */

/** Why a key does NOT justify interrupting a live session. */
export type InertReason = string;

/**
 * Telegram env keys whose CHANGE justifies respawning a live session.
 *
 * All of these decide **who may do what** — they are access control, and an
 * access-control change an operator has made and can see in the console must
 * not wait on an unrelated respawn. Everything else waits.
 */
export const TELEGRAM_RESTART_ON_CHANGE_ENV_KEYS = [
  /** Who may talk to the agent — and, at four MCP call sites, who it may send to. */
  'TELEGRAM_ALLOWED_CHATS',
  /** Drops every group / supergroup / channel message. A containment control. */
  'TELEGRAM_PRIVATE_CHATS_ONLY',
  /** Peer kill switch. A switch whose purpose is stopping something promptly. */
  'PEER_DISABLED',
  /** Derived boolean mirror of the same switch, kept for the rollout. */
  'TELEGRAM_PEER_DISABLED',
] as const;

/**
 * Telegram env keys deliberately NOT worth a restart, each with the reason.
 *
 * Being listed here is a claim that the change can wait for the next natural
 * respawn (day rollover, self-update, channel add, wedge recovery) — not that
 * the value is read fresh. None of them are.
 */
export const TELEGRAM_INERT_ENV_KEYS: Readonly<Record<string, InertReason>> = {
  AGT_AGENT_CODE_NAME: 'agent identity; cannot change for a live agent',
  AGT_HOST: 'control-plane URL; a deploy-time value, identical for every agent on the host',
  AGT_TURN_INITIATOR_FILE: 'a path derived from the code name, not a policy',
  TELEGRAM_BOT_TOKEN:
    'credential rotation. Deliberately excluded: a restart does not repair the window (the old token stops working the moment it is revoked, whether or not we respawn), and bouncing every session on a token write would make routine rotation an outage. Reconnect flow owns this.',
  TELEGRAM_ACK_REACTION: 'cosmetic — which emoji acknowledges an inbound message',
  TELEGRAM_SKIP_REACTION: 'cosmetic — which emoji marks a skipped message',
  TELEGRAM_PEERS: 'peer roster contents; a stale roster fails closed (an unknown peer is refused), so waiting is safe',
  TELEGRAM_PEER_GROUP_IDS: 'peer group ids; same fail-closed argument as the roster',
  TELEGRAM_PEER_AGENT_MODE: 'peer interaction mode; behaviour, not an access gate',
  TELEGRAM_PEERS_GATE:
    'per-peer gating. A judgement call, and the conservative one: it narrows an ALREADY-authorised peer set rather than admitting anyone new, and it churns with roster edits. Revisit if a gate change is ever needed urgently.',
  TELEGRAM_DIAGNOSTIC_CHAT_IDS:
    'who may run /investigate. Elevated, but read only inside the diagnostic command path and scoped to chats already past the allowlist above.',
  TELEGRAM_PING_ALLOWED_CHAT_IDS: 'who may run /ping; same argument as the diagnostic ids',
};

/** True when a change to `key` should respawn a live telegram session. */
export function telegramEnvChangeRequiresRestart(key: string): boolean {
  return (TELEGRAM_RESTART_ON_CHANGE_ENV_KEYS as readonly string[]).includes(key);
}

/**
 * The restart-relevant slice of a rendered channel env, canonicalised for
 * hashing.
 *
 * `TELEGRAM_ALLOWED_CHATS` is a comma-joined SET — the MCP builds a `Set` from
 * it and every read is a membership test — so its order carries no meaning.
 * Sorting and de-duplicating before hashing is not tidiness: an order-flapping
 * list oscillating a hash into ~10-minute respawns is the measured ENG-7435
 * incident, and ENG-8399 applies the same treatment to the derived principal-id
 * lists for exactly this reason.
 */
export function telegramPolicyEnvBasis(env: Readonly<Record<string, string>>): string {
  const parts: string[] = [];
  for (const key of [...TELEGRAM_RESTART_ON_CHANGE_ENV_KEYS].sort()) {
    const raw = env[key];
    if (raw === undefined) continue;
    const value =
      key === 'TELEGRAM_ALLOWED_CHATS'
        ? [...new Set(raw.split(',').map((v) => v.trim()).filter((v) => v.length > 0))]
            .sort()
            .join(',')
        : raw.trim();
    parts.push(`${key}=${value}`);
  }
  // An empty basis is meaningful — it is "no policy set" — and must compare
  // equal to itself across ticks rather than read as absent.
  return parts.join('\n');
}

/**
 * Rebuild the restart-relevant telegram env from the inputs the MANAGER holds,
 * mirroring the claudecode adapter's emission.
 *
 * The manager has `entry.config` plus its own computed `peerDisabledMode`; the
 * rendered env only exists inside the adapter. Rather than plumb the rendered
 * env back out, this reproduces the four restart-worthy emissions exactly as
 * `frameworks/claudecode/index.ts` writes them:
 *
 *   allowed_chats[]        -> TELEGRAM_ALLOWED_CHATS  (joined; empty ⇒ absent)
 *   private_chats_only===true -> TELEGRAM_PRIVATE_CHATS_ONLY='1'
 *   peerDisabledMode!=='off'  -> PEER_DISABLED=<mode>
 *   peerDisabledMode==='all'  -> TELEGRAM_PEER_DISABLED='true'  (legacy mirror)
 *
 * Faithfulness matters in one direction only: this must CHANGE whenever the
 * policy changes. It need not produce byte-identical strings to the adapter,
 * because {@link telegramPolicyEnvBasis} canonicalises before hashing — but a
 * missed input would silently reproduce the bug for that input, which is why
 * the mapping is written out rather than inferred.
 */
export function telegramPolicyEnvFromInputs(
  config: Readonly<Record<string, unknown>>,
  opts?: { peerDisabledMode?: string },
): Record<string, string> {
  const env: Record<string, string> = {};
  const allowedChats = config['allowed_chats'];
  if (Array.isArray(allowedChats)) {
    const ids = allowedChats
      .map((v) => (typeof v === 'string' || typeof v === 'number' ? String(v).trim() : ''))
      .filter((v) => v.length > 0);
    if (ids.length > 0) env['TELEGRAM_ALLOWED_CHATS'] = ids.join(',');
  }
  // Strict === true, mirroring the adapter and CS-1628: a truthy non-boolean
  // must not read as configured, or the policy is silently never enabled.
  if (config['private_chats_only'] === true) env['TELEGRAM_PRIVATE_CHATS_ONLY'] = '1';
  const mode = opts?.peerDisabledMode;
  if (mode && mode !== 'off') env['PEER_DISABLED'] = mode;
  if (mode === 'all') env['TELEGRAM_PEER_DISABLED'] = 'true';
  return env;
}

/**
 * True when the policy actually restricts anyone.
 *
 * Governs the unknown-baseline branch of the restart decision only (ENG-7771's
 * `currentPolicyRestrictive`): an unknown baseline plus a RESTRICTIVE policy on
 * a live session fails closed and respawns, because the running child may be
 * enforcing nothing; an unknown baseline with no restriction establishes the
 * baseline quietly, because there is no exposure and respawning would bounce
 * the fleet on the deploy that ships this.
 */
export function isTelegramPolicyRestrictive(env: Readonly<Record<string, string>>): boolean {
  return telegramPolicyEnvBasis(env).length > 0;
}
